// src/ai/providers/OpenAIProvider.ts
import axios from 'axios'
import { BaseAIProvider, AIRequestPayload, AIResponse, AIConfig, AIProviderModel } from '../IAIProvider'

export class OpenAIProvider extends BaseAIProvider {
  constructor(config: AIConfig, callbacks?: any) {
    super(config, callbacks)
  }

  async getAvailableModels(): Promise<AIProviderModel[]> {
    try {
      const res = await axios.get(`${this.config.baseUrl}/models`, {
        headers: { Authorization: `Bearer ${this.config.apiKey}` },
      })
      return (
        res.data?.data?.map((m: any) => ({
          name: m.id,
          raw: m,
        })) || []
      )
    } catch (error) {
      console.error('Failed to get OpenAI models:', error)
      return []
    }
  }

  async sendRequest(payload: AIRequestPayload, customRequestId?: string): Promise<AIResponse> {
    const requestId = this.getOrGenerateRequestId(customRequestId)

    try {
      const controller = this.recordRequestStart(requestId, payload)
      if (!controller) {
        throw new Error('Failed to create abort controller')
      }
      const res = await axios.post(`${this.config.baseUrl}/chat/completions`, payload, {
        headers: { Authorization: `Bearer ${this.config.apiKey}` },
        signal: controller.signal,
      })
      const response: AIResponse = {
        text: res.data.choices[0].message.content,
        requestId,
        usage: res.data.usage,
      }

      this.recordRequestComplete(requestId, response)
      return response
    } catch (error) {
      if (error instanceof Error) {
        this.recordRequestError(requestId, error)
      }
      throw error
    }
  }

  async sendStreamingRequest(
    payload: AIRequestPayload,
    onData: (data: AIResponse) => void,
    customRequestId?: string,
  ): Promise<void> {
    const requestId = this.getOrGenerateRequestId(customRequestId)
    try {
      const controller = this.recordRequestStart(requestId, payload)
      if (!controller) {
        throw new Error('Failed to create abort controller')
      }
      const response = await fetch(`${this.config.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.config.apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...payload, stream: true }),
        signal: controller.signal,
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error('No response body reader available')
      }

      const decoder = new TextDecoder()
      let accumulatedText = ''
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          const trimmed = line.trim()
          if (!trimmed.startsWith('data:')) continue
          const data = trimmed.slice(5).trim()
          if (!data || data === '[DONE]') continue
          try {
            const json = JSON.parse(data)
            const content = json?.choices?.[0]?.delta?.content || ''
            if (content) {
              accumulatedText += content
              onData({ text: content, requestId, usage: json?.usage })
            }
          } catch {
            // ignore partial json
          }
        }
      }

      this.recordRequestComplete(requestId, { text: accumulatedText, requestId })
    } catch (error) {
      if (error instanceof Error) {
        this.recordRequestError(requestId, error)
      }
      throw error
    }
  }
}
